import { Link } from "react-router-dom";
import {
  Phone, ArrowRight, MapPin, Zap, Building2, Users, ShieldCheck,
  CheckCircle2, Star, ArrowUpRight,
} from "lucide-react";
import { SITE, SERVICES, TOWNS, PROCESS, REASONS, ARTICLES } from "@/config/site";
import { Seo, localBusinessSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { UrgencyBlock, SectionHeading, FinalCTA } from "@/components/blocks";
import { trackEvent } from "@/lib/analytics";

const REASSURANCE = [
  { icon: Zap, label: "Intervention rapide" },
  { icon: MapPin, label: "Entreprise locale" },
  { icon: Building2, label: "Déplacement dans la Manche" },
  { icon: Users, label: "Particuliers, pros & collectivités" },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: ARTICLES.map((a) => ({
    "@type": "Question",
    name: a.title,
    acceptedAnswer: { "@type": "Answer", text: a.body[a.body.length - 1] },
  })),
};

export default function Home() {
  return (
    <>
      <Seo
        title="Destruction de nids de frelons dans la Manche | FRELONS50"
        description="FRELONS50 intervient dans la Manche pour la destruction des nids de frelons et de guêpes ainsi que le traitement des rats, souris et autres nuisibles. Contact rapide au 06 35 92 21 49."
        path="/"
        jsonLd={[localBusinessSchema, faqSchema]}
      />

      {/* HERO */}
      <section className="relative bg-brand-black text-white overflow-hidden flex items-center min-h-[86vh] md:min-h-0">
        <div className="absolute inset-0">
          <picture>
            <source media="(max-width: 767px)" srcSet="/images/hero-nest-mobile.webp" />
            <img
              src="/images/hero-nest-desktop.webp"
              alt="Nid de frelons sous le débord de toit d'une maison en pierre de la Manche"
              className="w-full h-full object-cover object-[68%_center] md:object-[75%_center]"
              fetchPriority="high"
              width="1600"
              height="1073"
            />
          </picture>
          {/* Voile sombre progressif : plus dense à gauche (derrière le texte) */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-black/92 via-brand-black/60 to-brand-black/25" />
          {/* Renfort haut sur mobile (zone du texte) + bas pour la transition */}
          <div className="absolute inset-0 bg-gradient-to-b from-brand-black/88 via-brand-black/45 to-transparent md:hidden" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-black/85 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-28">
          <Reveal className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-brand-gold/15 text-brand-gold border border-brand-gold/30 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
              <span className="w-2 h-2 rounded-full bg-brand-gold animate-pulse" /> {SITE.availability}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight [text-shadow:_0_2px_16px_rgba(0,0,0,0.5)]">
              Destruction de nids de frelons et traitement des nuisibles dans la Manche
            </h1>
            <p className="mt-6 text-lg text-white/90 leading-relaxed max-w-2xl [text-shadow:_0_1px_10px_rgba(0,0,0,0.55)]">
              Intervention rapide contre les frelons asiatiques, guêpes, rats, souris et autres nuisibles. Diagnostic,
              sécurisation et traitement adaptés à votre situation.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={SITE.phoneHref} onClick={() => trackEvent("click_phone", { location: "hero" })} data-testid="hero-call-btn" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-gold text-brand-black font-bold hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-10px_rgba(245,180,0,0.7)] transition-all">
                <Phone className="w-5 h-5" /> Appeler le {SITE.phoneDisplay}
              </a>
              <Link to="/contact" data-testid="hero-intervention-btn" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white/10 border-2 border-white/30 text-white font-bold backdrop-blur-md hover:bg-white/20 transition-colors">
                Demander une intervention <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <ul className="mt-10 flex flex-wrap gap-3">
              {REASSURANCE.map((r) => (
                <li key={r.label} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full text-sm font-medium">
                  <r.icon className="w-4 h-4 text-brand-gold" /> {r.label}
                </li>
              ))}
              {SITE.certibiocide.display && (
                <li className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full text-sm font-medium">
                  <ShieldCheck className="w-4 h-4 text-brand-gold" /> {SITE.certibiocide.label}
                </li>
              )}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* URGENCE */}
      <UrgencyBlock />

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <SectionHeading eyebrow="Nos interventions" title="Un traitement adapté à chaque nuisible" sub="Sélectionnez votre situation pour découvrir notre approche et nous contacter directement." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.05}>
              <Link to={`/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block h-full bg-brand-gray rounded-3xl overflow-hidden border border-transparent hover:border-brand-gold/40 hover:shadow-xl transition-all">
                <div className="relative h-44 overflow-hidden">
                  <img src={s.image} alt={s.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" width="600" height="400" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-black/40 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-xl font-bold text-brand-black">{s.nav}</h3>
                    <ArrowUpRight className="w-5 h-5 text-brand-text group-hover:text-brand-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <p className="mt-2 text-sm text-brand-text leading-relaxed">{s.short}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* POURQUOI NOUS */}
      <section className="bg-brand-gray py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading eyebrow="Pourquoi FRELONS50 ?" title="Un professionnel local, clair et réactif" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {REASONS.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.05} className="bg-white rounded-3xl p-7 h-full shadow-[0_8px_30px_rgba(0,0,0,0.05)]">
                <div className="w-10 h-10 rounded-full bg-brand-gold/15 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-5 h-5 text-brand-gold" />
                </div>
                <h3 className="font-display text-base font-bold text-brand-black">{r.title}</h3>
                <p className="mt-2 text-sm text-brand-text leading-relaxed">{r.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESSUS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <SectionHeading eyebrow="Déroulement" title="Comment se passe une intervention ?" sub="Un parcours simple et rassurant, du premier contact au suivi." />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {PROCESS.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.08} className="relative">
              <div className="bg-white border border-gray-100 rounded-3xl p-7 h-full shadow-[0_8px_30px_rgba(0,0,0,0.05)]">
                <span className="font-display text-5xl font-extrabold text-brand-gold/30">{p.step}</span>
                <h3 className="font-display text-lg font-bold text-brand-black mt-2">{p.title}</h3>
                <p className="mt-2 text-sm text-brand-text leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ZONE */}
      <section className="bg-brand-black text-white py-20 md:py-28 grain-overlay">
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading eyebrow="Zone d'intervention" title="Intervention dans la Manche" light sub="Nous intervenons dans le département de la Manche (50) et les communes limitrophes selon disponibilité." />
            <div className="flex flex-wrap gap-2.5">
              {TOWNS.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-4 py-2 rounded-full text-sm font-medium">
                  <MapPin className="w-3.5 h-3.5 text-brand-gold" /> {t}
                </span>
              ))}
            </div>
            <p className="mt-6 text-white/70 text-sm">
              Votre commune n'est pas indiquée ?{" "}
              <Link to="/contact" className="text-brand-gold font-semibold underline">Contactez-nous pour vérifier notre disponibilité.</Link>
            </p>
          </div>
          <Reveal className="flex justify-center">
            <img src="/images/manche-map.png" alt="Carte du département de la Manche avec les principales zones d'intervention" loading="lazy" className="w-full max-w-md" width="500" height="500" />
          </Reveal>
        </div>
      </section>

      {/* AVIS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <SectionHeading eyebrow="Avis clients" title="La confiance de nos clients locaux" center />
        <div className="max-w-2xl mx-auto text-center bg-brand-gray rounded-3xl p-10">
          <div className="flex justify-center gap-1 mb-4" aria-hidden="true">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 text-brand-gold fill-brand-gold" />)}
          </div>
          <p className="font-display text-xl font-bold text-brand-black">Votre satisfaction construit notre réputation locale.</p>
          <p className="mt-3 text-brand-text text-sm">Nous affichons uniquement des avis authentiques. Vous avez fait appel à nous ?</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {SITE.social.googleReviews ? (
              <a href={SITE.social.googleReviews} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-black text-white font-bold text-sm">Voir les avis Google</a>
            ) : null}
            {SITE.social.googleLeaveReview ? (
              <a href={SITE.social.googleLeaveReview} target="_blank" rel="noopener noreferrer" data-testid="leave-review-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-brand-black text-brand-black font-bold text-sm">Laisser un avis</a>
            ) : (
              <span className="text-xs text-brand-text">Les liens Google seront ajoutés prochainement.</span>
            )}
          </div>
        </div>
      </section>

      {/* CONSEILS */}
      <section className="bg-brand-gray py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading eyebrow="Conseils" title="Les bons réflexes en cas de nid" sub="Des conseils pédagogiques et prudents pour agir en sécurité." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ARTICLES.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.05}>
                <Link to={`/conseils/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group block h-full bg-white rounded-3xl overflow-hidden border border-transparent hover:border-brand-gold/40 hover:shadow-xl transition-all">
                  <div className="relative h-40 overflow-hidden">
                    <img src={a.image} alt={a.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" width="600" height="400" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-bold text-brand-black leading-snug">{a.title}</h3>
                    <p className="mt-2 text-sm text-brand-text leading-relaxed line-clamp-3">{a.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand-black group-hover:gap-2 transition-all">Lire le conseil <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <FinalCTA />
    </>
  );
}
